import React, { useMemo } from 'react';
import { useGenogram } from '../../contexts/GenogramContext';
import { ConnectionStatus, PlacementStatus, PLACEMENT_STATUS_CONFIG, CONNECTION_STATUS_CONFIG } from '../../constants/connectionStatus';

const DiscoveryPanel = ({ onClose }) => {
  const { state, actions } = useGenogram();
  
  // Filter placements and connections to show discoveries
  const discoveries = useMemo(() => {
    const items = [];
    
    // Add placement considerations from new placements array (PRIMARY - more critical)
    // Only show potential placements (temporary or permanent)
    state.placements.forEach(placement => {
      if (placement.placementStatus === PlacementStatus.POTENTIAL_TEMPORARY ||
          placement.placementStatus === PlacementStatus.POTENTIAL_PERMANENT) {
        items.push({
          type: 'placement',
          id: placement.id,
          placement,
          status: placement.placementStatus,
          metadata: placement.discoveryMetadata,
          priority: 1 // Higher priority
        });
      }
    });
    
    // BACKWARD COMPATIBILITY: Also check relationships for placement status
    // This supports the old system where placementStatus was on relationships
    state.relationships.forEach(rel => {
      if (rel.placementStatus === PlacementStatus.POTENTIAL_TEMPORARY ||
          rel.placementStatus === PlacementStatus.POTENTIAL_PERMANENT) {
        items.push({
          type: 'placement-legacy',
          id: rel.id,
          relationship: rel,
          status: rel.placementStatus,
          metadata: rel.discoveryMetadata || {},
          priority: 1 // Higher priority
        });
      }
    });
    
    // Add relationships with connection status (SECONDARY - less critical)
    state.relationships.forEach(rel => {
      if (rel.connectionStatus === ConnectionStatus.POTENTIAL ||
          rel.connectionStatus === ConnectionStatus.EXPLORING) {
        items.push({
          type: 'connection',
          id: rel.id,
          relationship: rel,
          status: rel.connectionStatus,
          metadata: rel.discoveryMetadata,
          priority: 2 // Lower priority
        });
      }
    });
    
    // Sort by priority (placement first), then by date (newest first)
    return items.sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      const dateA = a.metadata?.discoveryDate ? new Date(a.metadata.discoveryDate) : new Date(0);
      const dateB = b.metadata?.discoveryDate ? new Date(b.metadata.discoveryDate) : new Date(0);
      return dateB - dateA;
    });
  }, [state.placements, state.relationships]);
  
  // Count by type and status
  const placementCount = discoveries.filter(d => d.type === 'placement' || d.type === 'placement-legacy').length;
  const connectionCount = discoveries.filter(d => d.type === 'connection').length;
  const potentialTempCount = discoveries.filter(d => 
    (d.type === 'placement' || d.type === 'placement-legacy') && d.status === PlacementStatus.POTENTIAL_TEMPORARY
  ).length;
  const potentialPermCount = discoveries.filter(d => 
    (d.type === 'placement' || d.type === 'placement-legacy') && d.status === PlacementStatus.POTENTIAL_PERMANENT
  ).length;
  
  const getPersonName = (personId) => {
    const person = state.people.find(p => p.id === personId);
    if (!person) return 'Unknown';
    return person.name || 'Unknown';
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };
  
  const getStatusColor = (status, type) => {
    if (type === 'placement') {
      const config = PLACEMENT_STATUS_CONFIG[status];
      if (config) {
        return { 
          bg: `${config.color}20`, // 20 = 12% opacity in hex
          text: config.color, 
          label: config.label, 
          icon: config.icon 
        };
      }
      return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown', icon: '❓' };
    } else {
      const config = CONNECTION_STATUS_CONFIG[status];
      if (config) {
        return { 
          bg: `${config.color}20`, 
          text: config.color, 
          label: config.label, 
          icon: '�' 
        };
      }
      return { bg: '#f3f4f6', text: '#6b7280', label: 'Unknown', icon: '❓' };
    }
  };
  
  const handleConfirm = (discovery) => {
    if (discovery.type === 'placement') {
      // Move to current placement (temporary or permanent based on current status)
      const newStatus = discovery.status === PlacementStatus.POTENTIAL_PERMANENT 
        ? PlacementStatus.CURRENT_PERMANENT 
        : PlacementStatus.CURRENT_TEMPORARY;
      
      actions.updatePlacement(discovery.id, {
        placementStatus: newStatus,
        discoveryMetadata: {
          ...discovery.metadata,
          approvalDate: new Date().toISOString()
        }
      });
    } else if (discovery.type === 'placement-legacy') {
      // Legacy: Update relationship placement status
      const newStatus = discovery.status === PlacementStatus.POTENTIAL_PERMANENT 
        ? PlacementStatus.CURRENT_PERMANENT 
        : PlacementStatus.CURRENT_TEMPORARY;
      
      actions.updateRelationship(discovery.id, {
        placementStatus: newStatus,
        discoveryMetadata: {
          ...discovery.metadata,
          approvalDate: new Date().toISOString()
        }
      });
    } else {
      actions.updateRelationship(discovery.id, {
        connectionStatus: ConnectionStatus.CONFIRMED,
        discoveryMetadata: {
          ...discovery.metadata,
          confirmationDate: new Date().toISOString()
        }
      });
    }
  };
  
  const handleEdit = (discovery) => {
    if (discovery.type === 'placement') {
      // Open placement edit panel
      actions.setSelectedPlacement(discovery.id);
    } else {
      // Both legacy placements and connections use relationship panel
      actions.setSelectedRelationship(discovery.id);
    }
  };
  
  const handleRuleOut = (discovery) => {
    if (discovery.type === 'placement') {
      actions.updatePlacement(discovery.id, {
        placementStatus: PlacementStatus.RULED_OUT,
        discoveryMetadata: {
          ...discovery.metadata,
          ruledOutDate: new Date().toISOString()
        }
      });
    } else if (discovery.type === 'placement-legacy') {
      // Legacy: Update relationship placement status
      actions.updateRelationship(discovery.id, {
        placementStatus: PlacementStatus.RULED_OUT,
        discoveryMetadata: {
          ...discovery.metadata,
          ruledOutDate: new Date().toISOString()
        }
      });
    } else {
      actions.updateRelationship(discovery.id, {
        connectionStatus: ConnectionStatus.RULED_OUT,
        discoveryMetadata: {
          ...discovery.metadata,
          ruledOutDate: new Date().toISOString()
        }
      });
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: '400px',
      height: '100vh',
      background: 'white',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
      zIndex: 40,
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideInRight 0.3s ease-out'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937'
          }}>
            Discovery Tracking
          </h2>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '13px',
            color: '#6b7280'
          }}>
            Placements & connections in review
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '6px',
            color: '#6b7280',
            transition: 'all 0.2s'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.color = '#1f2937';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      
      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px'
      }}>
        {discoveries.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#9ca3af'
          }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
              style={{ margin: '0 auto 16px', opacity: 0.5 }}>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
            <p style={{ fontSize: '14px', margin: 0 }}>No pending discoveries</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>
              Potential placements and connections will appear here
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {discoveries.map(discovery => {
              const statusInfo = getStatusColor(discovery.status, discovery.type);
              const isPriority = discovery.type === 'placement' || discovery.type === 'placement-legacy';
              
              // Get display name(s)
              let displayName;
              if (discovery.type === 'placement') {
                const childName = getPersonName(discovery.placement.childId);
                const caregiverName = getPersonName(discovery.placement.caregiverId);
                displayName = `${childName} → ${caregiverName}`;
              } else if (discovery.type === 'placement-legacy') {
                // Legacy: placementStatus on relationship - show as relationship
                const person1Name = getPersonName(discovery.relationship.from);
                const person2Name = getPersonName(discovery.relationship.to);
                displayName = `${person1Name} ↔ ${person2Name}`;
              } else {
                const person1Name = getPersonName(discovery.relationship.from);
                const person2Name = getPersonName(discovery.relationship.to);
                displayName = `${person1Name} ↔ ${person2Name}`;
              }
              
              return (
                <div
                  key={`${discovery.type}-${discovery.id}`}
                  style={{
                    background: isPriority ? '#fafffe' : '#fafafa',
                    border: isPriority ? '2px solid #16a34a' : '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '14px',
                    transition: 'all 0.2s',
                    cursor: 'default',
                    position: 'relative'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = isPriority ? '0 4px 12px rgba(22,163,74,0.15)' : '0 2px 8px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = isPriority ? '#15803d' : '#d1d5db';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = isPriority ? '#16a34a' : '#e5e7eb';
                  }}
                >
                  {/* Priority indicator for placements */}
                  {isPriority && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#16a34a',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Priority
                    </div>
                  )}
                  
                  {/* Names */}
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '8px',
                    paddingRight: isPriority ? '70px' : '0'
                  }}>
                    {statusInfo.icon} {displayName}
                  </div>
                  
                  {/* Status Badge */}
                  <div style={{
                    display: 'inline-block',
                    background: statusInfo.bg,
                    color: statusInfo.text,
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    marginBottom: '10px'
                  }}>
                    {statusInfo.label}
                  </div>
                  
                  {/* Discovery Metadata */}
                  {discovery.metadata && (
                    <div style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      marginBottom: '10px',
                      lineHeight: '1.6'
                    }}>
                      {discovery.metadata.discoveryDate && (
                        <div>📅 {formatDate(discovery.metadata.discoveryDate)}</div>
                      )}
                      {discovery.metadata.discoverySource && (
                        <div>📍 {discovery.metadata.discoverySource}</div>
                      )}
                      {discovery.metadata.discoveryNotes && (
                        <div style={{ marginTop: '6px', fontStyle: 'italic' }}>
                          "{discovery.metadata.discoveryNotes}"
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '6px',
                    marginTop: '12px'
                  }}>
                    <button
                      onClick={() => handleConfirm(discovery)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#059669'}
                      onMouseLeave={e => e.currentTarget.style.background = '#10b981'}
                      title={discovery.type === 'placement' ? 'Approve placement' : 'Confirm connection'}
                    >
                      ✓ {discovery.type === 'placement' ? 'Approve' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => handleEdit(discovery)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#2563eb'}
                      onMouseLeave={e => e.currentTarget.style.background = '#3b82f6'}
                      title="Edit details"
                    >
                      ✎ Edit
                    </button>
                    <button
                      onClick={() => handleRuleOut(discovery)}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#dc2626'}
                      onMouseLeave={e => e.currentTarget.style.background = '#ef4444'}
                      title={discovery.type === 'placement' ? 'Mark not suitable' : 'Rule out connection'}
                    >
                      ✕ {discovery.type === 'placement' ? 'Not Suitable' : 'Rule Out'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer Summary */}
      {discoveries.length > 0 && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid #e5e7eb',
          background: '#f9fafb',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: '600', color: '#16a34a', marginBottom: '4px', fontSize: '13px' }}>🏠 Placements (Priority)</div>
            <div style={{ display: 'flex', gap: '12px', paddingLeft: '8px', fontSize: '11px' }}>
              <span>Temp: <strong style={{ color: '#3b82f6' }}>{potentialTempCount}</strong></span>
              <span>Perm: <strong style={{ color: '#8b5cf6' }}>{potentialPermCount}</strong></span>
              <span>Total: <strong>{placementCount}</strong></span>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>🔗 Connections</div>
            <div style={{ paddingLeft: '8px' }}>
              <span>Active: <strong>{connectionCount}</strong></span>
            </div>
          </div>
        </div>
      )}
      
      {/* Add slide-in animation */}
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DiscoveryPanel;
